// hooks/useDMCAGenerator.js
import { useState } from 'react';
import { findAgent } from '../dmcaAgents';

const useDMCAGenerator = (planTracker) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState('');
  const [generatedLetters, setGeneratedLetters] = useState([]);
  const [aiResponse, setAiResponse] = useState('');

  const generateDMCALetter = async (formData) => {
    setIsProcessing(true);
    setProcessingError('');
    setAiResponse('');

    if (planTracker) {
      const canGenerate = planTracker.canGenerateQR();
      if (!canGenerate) {
        const error = planTracker.getLimitReason() || 'You have reached your limit. Please upgrade.';
        setProcessingError(error);
        setIsProcessing(false);
        throw new Error(error);
      }
    }

    const apiKey = process.env.REACT_APP_GROQ_API_KEY;
    if (!apiKey) {
      const error = 'Groq API key missing. Add REACT_APP_GROQ_API_KEY to your .env file';
      setProcessingError(error);
      setIsProcessing(false);
      throw new Error(error);
    }

    try {
      const today = new Date().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });

      const knownAgent = findAgent(formData.platform);
      const agentBlock = knownAgent
        ? `DMCA Agent: ${knownAgent.agent}\nAgent Address: ${knownAgent.address}\nAgent Email: ${knownAgent.email}`
        : `Platform/Host: ${formData.platform}`;

      const systemPrompt = `You are an expert legal document writer specializing in DMCA takedown notices under the Digital Millennium Copyright Act (17 U.S.C. 512). Generate professional, legally-structured DMCA takedown notices that are compliant and effective.

IMPORTANT — USER INPUT PROFESSIONALIZATION:
The user's work description and other inputs may be written casually or informally (e.g. "my video is stolen", "someone copied my pic", "they took my song"). You MUST silently rewrite these into formal, professional legal language while preserving the exact facts and context. Never use slang, casual phrasing, or first-draft wording from the user in the final letter. For example:
- "my video is stolen" → "my original video content has been reproduced and published without authorization"
- "someone copied my pic" → "my original photographic work has been copied and displayed without my permission"
- "they took my song" → "my original musical composition has been reproduced without license or authorization"
Apply this same professionalization to all user-provided fields (description, platform name, URLs context, etc.) as needed.

The notice MUST include all required elements under 17 U.S.C. 512(c)(3):
1. Identification of the copyrighted work claimed to have been infringed
2. Identification of the material to be removed and its location
3. Contact information of the complaining party
4. A single good faith belief statement
5. A single statement of accuracy and authority under penalty of perjury — include this ONLY ONCE, in the closing paragraph before "Sincerely"

CRITICAL FORMATTING RULES:
- Do NOT duplicate the perjury statement. Include it exactly once, in the final paragraph before the closing.
- The letter must end in this exact order:
  1. The closing paragraph containing the perjury/accuracy declaration
  2. "Sincerely,"
  3. A blank line
  4. The claimant's printed full name
  5. A blank line
  6. [SIGNATURE]
- Do NOT place [SIGNATURE] anywhere else in the letter — only at the very end, after the printed name.
- Output only the letter itself with no explanations, preamble, or commentary.`;

      const userPrompt = `Generate a complete DMCA takedown notice with the following details:

CLAIMANT:
Name: ${formData.claimantName}
Email: ${formData.claimantEmail}
Address: ${formData.claimantAddress || 'Not provided'}

ORIGINAL WORK:
Description: ${formData.workDescription}
Type: ${formData.workType}
URL: ${formData.originalWorkUrl}

INFRINGING CONTENT URL: ${formData.infringingUrl}

RECIPIENT:
${agentBlock}

Date: ${today}

Generate a complete ready-to-send DMCA notice addressed to the recipient. The letter must close with the perjury declaration once, then "Sincerely," then the claimant's printed name on a new line, then [SIGNATURE] on a new line at the very end.`;

      const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'llama-3.3-70b-versatile',
          temperature: 0.2,
          max_tokens: 2000,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ]
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to generate letter');
      }

      const data = await response.json();
      const letterText = data.choices[0]?.message?.content?.trim();
      if (!letterText) throw new Error('No letter generated. Please try again.');

      if (planTracker) {
        const consumed = await planTracker.consumeCredit();
        if (!consumed) throw new Error('Failed to track usage. Please try again.');
      }

      setAiResponse(letterText);

      const newLetter = {
        id: Date.now(),
        letterText,
        formData,
        knownAgent,
        timestamp: new Date().toISOString(),
        platform: formData.platform,
        infringingUrl: formData.infringingUrl,
      };

      setGeneratedLetters(prev => [newLetter, ...prev]);
      return newLetter;

    } catch (error) {
      setProcessingError(error.message || 'Failed to generate DMCA letter');
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteLetter = (id) => setGeneratedLetters(prev => prev.filter(l => l.id !== id));

  const clearAll = () => {
    setGeneratedLetters([]);
    setAiResponse('');
    setProcessingError('');
  };

  return {
    isProcessing,
    processingError,
    generatedLetters,
    aiResponse,
    generateDMCALetter,
    deleteLetter,
    clearAll,
    setGeneratedLetters,
  };
};

export default useDMCAGenerator;